import "./ScheduleCheckInDrawer.css";
import "antd/dist/reset.css";
import { FaCalendarAlt, FaClock, FaPhone, FaVideo } from "react-icons/fa";
import { TimePicker } from "antd";
import { toast } from "react-toastify";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import Select from "react-select";
import { useCareManagerClients } from "../outlets/Clients";

const DURATION_OPTIONS = [
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "60 minutes", value: 60 },
];

const formatDisplayDate = (dateValue) => {
  if (!dateValue) return "";
  return dayjs(dateValue).format("MM/DD/YYYY");
};

const ScheduleCheckInDrawer = ({
  fetchAppointmentData,
  selectedDate,
  selectedTime,
  setShowAddAppointment,
}) => {
  const user = useSelector((state) => state?.user);
  const dateInputRef = useRef(null);

  const [appointmentMethod, setAppointmentMethod] = useState("video");
  const [date, setDate] = useState("");
  const [duration, setDuration] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [time, setTime] = useState("");
  const [timeValue, setTimeValue] = useState(null);

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;
  const currentUserProviderId = user.user?.providerId;

  const { data: careManagerClients, loading: patientsLoading } =
    useCareManagerClients();

  const patientOptions = useMemo(
    () =>
      (careManagerClients || [])
        .filter((client) => client.userId || client._id)
        .map((client) => ({
          label: `${client.name}${
            client.accessCode ? ` (${client.accessCode})` : ""
          }`,
          value: client.userId || client._id,
        })),
    [careManagerClients]
  );

  useEffect(() => {
    setSelectedPatient(null);
    setDuration("");
    setAppointmentMethod("video");

    if (!selectedDate) {
      setDate("");
      setTime("");
      setTimeValue(null);
      return;
    }

    setDate(dayjs(selectedDate).format("YYYY-MM-DD"));

    if (selectedTime) {
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const timeString = `${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}`;
      setTime(timeString);
      setTimeValue(
        dayjs(selectedDate)
          .hour(hours)
          .minute(minutes)
          .second(0)
          .millisecond(0)
      );
      return;
    }

    setTime("");
    setTimeValue(null);
  }, [selectedDate, selectedTime]);

  useEffect(() => {
    const forceZIndex = () => {
      document.querySelectorAll(".ant-picker-dropdown").forEach((popup) => {
        popup.style.zIndex = "10001";
      });
    };

    forceZIndex();
    const observer = new MutationObserver(forceZIndex);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const handleClose = () => {
    setShowAddAppointment(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedPatient) {
      toast.error("Please select a patient.");
      return;
    }

    if (!date) {
      toast.error("Please select a date.");
      return;
    }

    if (!time) {
      toast.error("Please select a time.");
      return;
    }

    if (!duration) {
      toast.error("Please select a duration.");
      return;
    }

    if (!currentUserProviderId) {
      toast.error("Provider ID not found. Please try again.");
      return;
    }

    setIsLoading(true);
    const authToken = user.user?.token;
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/appointment/create?businessId=${businessId || ""}&businessIdentifier=${businessIdentifier || ""}`,
        {
          appointmentMethod,
          date: dayjs(date).format("MM/DD/YYYY"),
          duration: Number(duration),
          selectedProvider: currentUserProviderId,
          selectedUser: selectedPatient.value,
          time,
          timeZone: clientTimeZone,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Appointment scheduled successfully.");
        fetchAppointmentData?.();
        setShowAddAppointment(false);
      } else {
        toast.error(response.data.message || "Could not create appointment.");
      }
    } catch (error) {
      console.error("ScheduleCheckInDrawer submit error:", error);
      toast.error(
        error.response?.data?.message || "Failed to create appointment."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cm-schedule-drawer-overlay" onClick={handleClose}>
      <aside
        className="cm-schedule-drawer"
        onClick={(event) => event.stopPropagation()}>
        <div className="cm-schedule-drawer-header">
          <h2 className="cm-schedule-drawer-title">Schedule Check-in</h2>
          <button
            className="cm-schedule-drawer-close"
            onClick={handleClose}
            type="button">
            Close
          </button>
        </div>

        <form className="cm-schedule-drawer-form" onSubmit={handleSubmit}>
          <div className="cm-schedule-drawer-body">
            <div className="cm-schedule-field">
              <label htmlFor="schedule-patient">Patient</label>
              <Select
                classNamePrefix="cm-schedule-select"
                inputId="schedule-patient"
                isClearable
                isLoading={patientsLoading}
                isSearchable
                onChange={setSelectedPatient}
                options={patientOptions}
                placeholder="Select patient"
                value={selectedPatient}
              />
            </div>

            <div className="cm-schedule-field">
              <label htmlFor="schedule-date">Date</label>
              <div
                className={`cm-schedule-input-shell${
                  date ? "" : " is-placeholder"
                }`}
                onClick={() => {
                  try {
                    dateInputRef.current?.showPicker();
                  } catch {
                    dateInputRef.current?.focus();
                  }
                }}>
                <input
                  ref={dateInputRef}
                  className="cm-schedule-native-input"
                  id="schedule-date"
                  min={dayjs().format("YYYY-MM-DD")}
                  onChange={(event) => setDate(event.target.value)}
                  type="date"
                  value={date}
                />
                <span>{date ? formatDisplayDate(date) : "Select date"}</span>
                <FaCalendarAlt className="cm-schedule-field-icon" />
              </div>
            </div>

            <div className="cm-schedule-field">
              <label htmlFor="schedule-time">Time</label>
              <TimePicker
                className="cm-schedule-time-picker"
                format="h:mm a"
                hourStep={1}
                id="schedule-time"
                minuteStep={15}
                onChange={(value) => {
                  if (value) {
                    setTime(value.format("HH:mm"));
                    setTimeValue(value);
                  } else {
                    setTime("");
                    setTimeValue(null);
                  }
                }}
                placeholder="Select time"
                suffixIcon={<FaClock className="cm-schedule-field-icon" />}
                use12Hours
                value={timeValue}
              />
            </div>

            <div className="cm-schedule-field">
              <label htmlFor="schedule-duration">Duration</label>
              <select
                className="cm-schedule-duration-select"
                id="schedule-duration"
                onChange={(event) => setDuration(event.target.value)}
                required
                value={duration}>
                <option disabled value="">
                  Select duration
                </option>
                {DURATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="cm-schedule-field">
              <label>Method</label>
              <div className="cm-schedule-method-toggle">
                <button
                  className={`cm-schedule-method-btn${
                    appointmentMethod === "video" ? " is-active" : ""
                  }`}
                  onClick={() => setAppointmentMethod("video")}
                  type="button">
                  <FaVideo />
                  Video
                </button>
                <button
                  className={`cm-schedule-method-btn${
                    appointmentMethod === "phone" ? " is-active" : ""
                  }`}
                  onClick={() => setAppointmentMethod("phone")}
                  type="button">
                  <FaPhone />
                  Phone
                </button>
              </div>
            </div>
          </div>

          <div className="cm-schedule-drawer-footer">
            <button
              className="cm-schedule-btn-cancel"
              disabled={isLoading}
              onClick={handleClose}
              type="button">
              Cancel
            </button>
            <button
              className="cm-schedule-btn-save"
              disabled={isLoading}
              type="submit">
              {isLoading ? "Scheduling..." : "Schedule"}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
};

export default ScheduleCheckInDrawer;
